// Ch76 wiring · the first consumer of GET /analytics/admin-truth.
//
// One band, five figures, one declared filter set: outstanding, overdue,
// collected this month, median days-to-pay, unclaimed leads. Each figure
// arrives as an envelope ({value, status, definition_id, computed_at}) and is
// rendered by that envelope's rules:
//
//   status "ok"       -> the number, formatted for its unit.
//   status "unknown"  -> the word "unknown", amber, with the backend's note.
//                        NEVER a zero — the whole reason admin_truth.py exists
//                        is that the old dashboard rendered failed queries as
//                        confident zeros.
//
// Self-fetching on purpose: AdminDashboard's Promise.all is the documented
// failure mechanism this band replaces, so it must not join it.

import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";

import InboundRepliesCard from "./InboundRepliesCard";
import {
  fetchAdminTruth,
  type AdminTruthEnvelope,
  type AdminTruthResponse,
  type AdminTruthSnapshot,
} from "../../services/adminTruthService";

/** The five figures this band exists to retire the SQL for, in read order. */
const TILE_SPECS: { metricId: string; label: string; unit: "usd" | "days" | "count" }[] = [
  { metricId: "money.outstanding_amount", label: "Outstanding", unit: "usd" },
  { metricId: "money.overdue_amount", label: "Overdue", unit: "usd" },
  { metricId: "money.collected_this_month", label: "Collected this month", unit: "usd" },
  { metricId: "money.median_days_to_pay", label: "Median days to pay", unit: "days" },
  { metricId: "leads.unclaimed", label: "Unclaimed leads", unit: "count" },
];

export function formatTruthValue(
  value: number | null,
  unit: "usd" | "days" | "count",
): string {
  // A null or non-finite value inside an "ok" envelope is a failed measurement
  // wearing a success costume (same rule as ops/Truth.tsx) — say so.
  if (value == null || !Number.isFinite(value)) return "unknown";
  if (unit === "usd") {
    return value.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    });
  }
  if (unit === "days") return `${value} d`;
  return String(value);
}

/**
 * Ch76 · the DoD MONEY row's second instrument, "snapshotted daily", on the
 * same screen as the figures. Three honest states, no fourth:
 *   ok + today_exists      -> "snapshot today ✓"
 *   ok + not today_exists  -> "no snapshot today" (+ the latest date, or "yet")
 *   unknown / missing      -> "snapshot — unknown" + the backend's note
 * A missing block (older backend) is unknown, never "no snapshot".
 */
export function snapshotHeadline(snapshot: AdminTruthSnapshot | undefined): { text: string; ok: boolean } {
  if (!snapshot || snapshot.status !== "ok" || snapshot.today_exists == null) {
    const note = snapshot?.note ? ` (${snapshot.note})` : "";
    return { text: `snapshot — unknown${note}`, ok: false };
  }
  if (snapshot.today_exists) return { text: "snapshot today ✓", ok: true };
  return {
    text: snapshot.latest_date ? `no snapshot today — latest ${snapshot.latest_date}` : "no snapshot yet",
    ok: false,
  };
}

function Tile({
  label,
  unit,
  envelope,
}: {
  label: string;
  unit: "usd" | "days" | "count";
  envelope: AdminTruthEnvelope | undefined;
}) {
  const unknown =
    !envelope || envelope.status !== "ok" || envelope.value == null || !Number.isFinite(envelope.value);
  const body = unknown ? "unknown" : formatTruthValue(envelope!.value, unit);
  const note = envelope?.note || (envelope ? undefined : "metric missing from response");
  return (
    <Box data-testid={`admin-truth-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Tooltip title={envelope ? `definition: ${envelope.definition_id}` : ""} placement="top">
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ color: unknown ? "warning.main" : "text.primary", lineHeight: 1.3 }}
        >
          {body}
        </Typography>
      </Tooltip>
      {unknown && note && (
        <Typography variant="caption" sx={{ color: "text.disabled", display: "block" }}>
          {note}
        </Typography>
      )}
    </Box>
  );
}

const AdminTruthBand: React.FC = () => {
  const [truth, setTruth] = React.useState<AdminTruthResponse | null>(null);
  const [state, setState] = React.useState<"loading" | "ok" | "error">("loading");

  const load = React.useCallback(async () => {
    setState("loading");
    try {
      setTruth(await fetchAdminTruth());
      setState("ok");
    } catch {
      setTruth(null);
      setState("error");
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  return (
    <Card sx={{ mb: 3 }} data-testid="admin-truth-band">
      <CardContent>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Typography variant="overline" color="text.secondary" fontWeight={700}>
            Money + leads — one filter set
          </Typography>
          {state === "ok" && truth && (
            <Typography variant="caption" color="text.disabled">
              as of {new Date(truth.generated_at).toLocaleString()}
            </Typography>
          )}
          {state === "ok" && truth && (() => {
            const line = snapshotHeadline(truth.snapshot);
            return (
              <Typography
                variant="caption"
                sx={{ color: line.ok ? "success.main" : "warning.main" }}
                data-testid="admin-truth-snapshot"
              >
                {line.text}
              </Typography>
            );
          })()}
        </Box>

        {state === "loading" && (
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            {TILE_SPECS.map((t) => (
              <Grid item xs={6} md={2.4} key={t.metricId}>
                <Skeleton width={90} height={18} />
                <Skeleton width={70} height={28} />
              </Grid>
            ))}
          </Grid>
        )}

        {state === "error" && (
          <Box mt={1} display="flex" alignItems="center" gap={1.5}>
            <Typography variant="body2" color="warning.main" data-testid="admin-truth-error">
              Couldn't read the admin-truth figures. Nothing here is being estimated in their place.
            </Typography>
            <Button size="small" onClick={() => void load()}>
              Retry
            </Button>
          </Box>
        )}

        {state === "ok" && truth && (
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            {TILE_SPECS.map((t) => (
              <Grid item xs={6} md={2.4} key={t.metricId}>
                <Tile label={t.label} unit={t.unit} envelope={truth.metrics[t.metricId]} />
              </Grid>
            ))}
          </Grid>
        )}

      {/* Ch83 · are renters' replies LANDING? It rides on THIS read rather than
          fetching its own: two reads of the same table in one screen is how two
          figures start disagreeing inside one page.
          A card, not a tile, because the payload is not a scalar — the number that
          separates "quiet" from "broken" is the AGE of the most recent reply, and a
          tile showing only a count would hide exactly the figure this metric was
          built to surface. */}
      {state === "ok" && truth && (
        <Box sx={{ mt: 2 }}>
          <InboundRepliesCard envelope={truth.metrics["messages.inbound_sms"]} />
        </Box>
      )}
      </CardContent>
    </Card>
  );
};

export default AdminTruthBand;
